import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { Orders } from '../../interfaces/orders';

/**
 * OrdersStateService
 * Gestiona el estado centralizado de las órdenes
 * Responsabilidad: State Management
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersStateService {
  // Estado privado
  private ordersSubject = new BehaviorSubject<Orders[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private selectedOrderSubject = new BehaviorSubject<Orders | null>(null);

  // 🔄 FASE B1 Parte 2: Observables optimizados con shareReplay y distinctUntilChanged
  public readonly orders$: Observable<Orders[]> = this.ordersSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  
  public readonly loading$: Observable<boolean> = this.loadingSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  
  public readonly selectedOrder$: Observable<Orders | null> = this.selectedOrderSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor() {
  }

  /**
   * Establece la lista completa de órdenes
   */
  setOrders(orders: Orders[]): void {
    this.ordersSubject.next(orders);
  }

  /**
   * Obtiene las órdenes actuales (snapshot)
   */
  getCurrentOrders(): Orders[] {
    return this.ordersSubject.value;
  }

  /**
   * Actualiza una orden específica por referencia
   */
  updateOrder(reference: string, updates: Partial<Orders>): void {
    const currentOrders = this.getCurrentOrders();
    const updatedOrders = currentOrders.map(order => 
      order.reference === reference ? { ...order, ...updates } : order
    );
    this.setOrders(updatedOrders);
  }

  /**
   * Elimina una orden por referencia
   */
  deleteOrder(reference: string): void {
    const currentOrders = this.getCurrentOrders();
    const filteredOrders = currentOrders.filter(order => order.reference !== reference);
    this.setOrders(filteredOrders);
  }

  /**
   * Agrega una nueva orden
   */
  addOrder(order: Orders): void {
    const currentOrders = this.getCurrentOrders();
    this.setOrders([order, ...currentOrders]);
  }

  /**
   * Establece el estado de carga
   */
  setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  /**
   * Establece la orden seleccionada
   */
  setSelectedOrder(order: Orders | null): void {
    this.selectedOrderSubject.next(order);
  }

  /**
   * Obtiene la orden seleccionada actual
   */
  getSelectedOrder(): Orders | null {
    return this.selectedOrderSubject.value;
  }

  /**
   * Limpia el estado
   */
  clear(): void {
    this.ordersSubject.next([]);
    this.selectedOrderSubject.next(null);
    this.setLoading(false);
  }

  /**
   * Busca una orden por referencia
   */
  findByReference(reference: string): Orders | undefined {
    return this.getCurrentOrders().find(order => order.reference === reference);
  }
}
